import type { ApiJobSynonymId } from '#/api/types/ApiTypedId';
import { Box } from '#/components/Box/Box';
import { LegacyButton } from '#/components/LegacyButton/LegacyButton';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SalariesJobTitle } from '#/modules/salary/types/SalariesJobTitle';
import { colors, fontWeights } from '#/theme/theme';

import { Card } from './Card';
import { Container } from './Container';
import { BigButton, StyledLinkButton } from './JobSelector.styled';

type Props = {
  suggestion?: { name: string; jobTitle: SalariesJobTitle; objectID: string };
  onSelect: (arg0: {
    name: string;
    familyId: string;
    id: string;
    synonymId: ApiJobSynonymId;
  }) => void;
  title: string;
  onContinue?: () => void;
};

export function NoJobsMatch({
  suggestion,
  onSelect,
  title,
  onContinue,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Container>
      <Card
        content={{
          name: (
            <>
              {i18n('We don’t currently have a match for this job title')}
              &hellip;
            </>
          ),
          body:
            suggestion &&
            /* TRANSLATORS: salary survey, continue with free text job title */
            i18n('Did you mean {{link}}%(title)s{{/link}}?', {
              title: suggestion.name,
              link: (
                <StyledLinkButton
                  qaId="suggestion-link"
                  onClick={() =>
                    onSelect({
                      name: suggestion.name,
                      id: suggestion.jobTitle.id,
                      familyId: suggestion.jobTitle.familyId,
                      synonymId: suggestion.objectID,
                    })
                  }
                />
              ),
            }),
          description: suggestion
            ? undefined
            : i18n('Try searching for a related or more general title'),
        }}
      />
      {onContinue && (
        <BigButton
          component={LegacyButton}
          qaId="continue-link"
          onClick={onContinue}
        >
          <Box py={[12, null, 24]} px={[24, null, 44]}>
            {
              /* TRANSLATORS: salary survey, continue with free text job title */
              i18n('Continue with {{link}}%(title)s{{/link}}', {
                title,
                link: (
                  <Box
                    component="span"
                    fontWeight={fontWeights.semiBold}
                    color={colors.brandBlue}
                  />
                ),
              })
            }
          </Box>
        </BigButton>
      )}
    </Container>
  );
}
